<script setup lang="ts" generic="Item extends SelectItem, Multiple extends boolean = false">
import { computed, nextTick, useTemplateRef, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import type { SelectItem, SelectItemSeparator } from '../../lib/list-selection/types';
import ActionButton from '@/components/base-combobox/action-button.vue';
import { trlMessage } from '../../lib/i18n';
import BaseIcon from '../../legacy/ui/base-icon/icon.vue';
import BaseDropdown from '../../legacy/ui/base-dropdown/base-dropdown.vue';
import DropdownMenuLayout from '../../legacy/ui/dropdown-menu-layout/dropdown-menu-layout.vue';
import BaseSelectList from './base-select-list.vue';

type Props = {
  items: Array<Item | SelectItemSeparator>;
  placeholder?: string;
  showCheckbox?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  multiline?: boolean;
  size?: string;
  disabled?: boolean;
  multiple?: Multiple;
};

type Id = Item['id'];

type ModelValue<Multiple extends boolean> = Multiple extends true
  ? Id[]
  : Id;

defineOptions({
  inheritAttrs: false
});

const props = defineProps<Props>();
const emit = defineEmits<{
  dropdownChange: [];
  clear: [];
  itemClick: [Item];
}>();

const value = defineModel<ModelValue<Multiple>>({
  default: []
});

const itemsMap = computed(() => {
  return new Map(
    props.items
      .filter((item): item is Item => item.type !== 'separator')
      .map((e) => [e.id, e])
  );
});

const base = useTemplateRef('base');
const dropdownSize = ref<number | 'large'>('large');

useResizeObserver(base, ([{ target }]) => {
  if (target.offsetWidth) {
    dropdownSize.value = target.offsetWidth;
  }
});

// Native form support
const hiddenInputValue = computed(() => {
  return value.value ?? undefined;
});

function handleItemClick(item: Item, hide: () => void) {
  emit('itemClick', item);
  if (props.multiple) {
    return;
  }
  hide();
  // Return focus to the trigger after picking an option. Otherwise Safari's
  // focus-fixup moves focus to the nearest input once the dropdown layer is
  // removed from the DOM (https://bugs.webkit.org/show_bug.cgi?id=278455).
  nextTick(() => base.value?.focus());
}
</script>

<template>
  <base-dropdown @change="emit('dropdownChange')">
    <template #default="{ toggle: dropdownToggle }">
      <slot
        name="toggle"
        :toggle="dropdownToggle"
      >
        <input
          type="hidden"
          :name="$attrs.name"
          :value="hiddenInputValue"
        >
        <button
          ref="base"
          :class="[
            $style.button, {
              [$style.disabled]: disabled,
              [$style.invalid]: invalid
            }
          ]"
          v-bind="$attrs"
          type="button"
          :disabled="disabled"
          @click="dropdownToggle"
        >
          <span :class="$style.text">
            {{ itemsMap.get(value)?.name || placeholder }}
          </span>
          <span :class="$style.iconWrapper">
            <slot name="icon">
              <base-icon type="chevron-down-24" />
            </slot>
          </span>
        </button>
      </slot>
    </template>
    <template #content="{ hide }">
      <dropdown-menu-layout :size="size || dropdownSize">
        <template #sub-heading>
          <div
            v-if="clearable"
            :class="$style.sub"
          >
            <action-button
              data-qa="reset"
              @click="emit('clear')"
            >
              {{ trlMessage('autocomplete.reset') }}
            </action-button>
          </div>
        </template>
        <template #content>
          <base-select-list
            v-model="value"
            :gap="0"
            :items="items"
            :multiple="multiple"
            :multiline="multiline"
            :show-checkbox="showCheckbox"
            :class="$style.select"
            @item-click="handleItemClick($event, hide)"
          >
            <template
              #icon="{ item }"
            >
              <slot
                name="item-icon"
                :item="item"
              />
            </template>
            <!-- Forwarded only when asked for: base-select-list falls back to its own
                 base-select-item, and an always-present template would blank the list.
                 `select` reproduces the default pick — apply the value, close, refocus. -->
            <template
              v-if="$slots.item"
              #item="{ item, index, onHover, toggle }"
            >
              <slot
                name="item"
                :item="item"
                :index="index"
                :on-hover="onHover"
                :select="() => { toggle(item); handleItemClick(item, hide); }"
              />
            </template>
          </base-select-list>
        </template>
      </dropdown-menu-layout>
    </template>
  </base-dropdown>
</template>

<style module>
.button {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  background: #fff;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
  outline: 0 none;
  padding: 0 8px 0 16px;
  vertical-align: top;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: default;
  user-select: none;
  overflow: hidden;
}

.invalid {
  border: 1px solid var(--hf-color-input-border-error);
  &:focus {
    border: 1px solid var(--hf-color-input-border-error-focus-visible);
  }
}

.sub {
  padding: var(--hf-spacing-m) var(--hf-spacing-l);
  border-bottom: 1px solid var(--hf-color-divider-default);
}

.disabled{
  opacity: .5;
}

.text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.iconWrapper {
  flex: 0 0 auto;
  display: inline-flex;
}

.select {
  width: 100%;
  padding: 8px;
}
</style>

<i18n lang="json">{}</i18n>
